import { Stack, TextField } from "@mui/material";
import { ControlledModificationReasonField } from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import { useRateConfiguration } from "@src/appV2/ExperimentalHomeHealth/lib/useRateConfiguration";
import { Text } from "@src/appV2/redesign/components/Text";

import { useFormatWorkplaceDateInput } from "../api/useFormatTimestampForWorkplace";
import { getDeadlineGraceHint } from "../CaseDashboard/forms/deadlineGraceHint";
import { PricingType, VisitType, type WorkerRequirements } from "../types";

export interface AddVisitStep3Data {
  visitsPerWeek: string;
  totalWeeks: string;
  firstVisitDueDate: string;
  estimatedDuration: string;
  chargePerVisit: string;
  payPerVisit: string;
  modificationReason: string;
}

interface Props {
  data: AddVisitStep3Data;
  pricingType: PricingType;
  visitType: VisitType | undefined;
  workerRequirement: WorkerRequirements | undefined;
  patientAddress: string;
  onChange: (field: keyof AddVisitStep3Data, value: string) => void;
}

const inputSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
  width: "100%",
};

export function MobileAddVisitStep3({
  data,
  pricingType,
  visitType,
  workerRequirement,
  patientAddress,
  onChange,
}: Props) {
  const rateLabel = pricingType === PricingType.PER_HOUR ? "per hour" : "per visit";
  const rates = useRateConfiguration({
    visitType,
    workerRequirements: workerRequirement,
    pricingType,
    patientAddress,
  });

  function handleChargeChange(value: string) {
    onChange("chargePerVisit", value);
    if (rates.canSetRates && value !== "") {
      const charge = Number.parseFloat(value);
      if (!Number.isNaN(charge)) {
        const takeRate = rates.takePercent / 100;
        if (takeRate < 0 || takeRate >= 1) {
          onChange("payPerVisit", "");
          return;
        }
        const pay = (charge * (1 - takeRate)).toFixed(2);
        onChange("payPerVisit", pay);
      }
    }
  }

  function handlePayChange(value: string) {
    onChange("payPerVisit", value);
    if (rates.canSetRates && value !== "") {
      const pay = Number.parseFloat(value);
      if (!Number.isNaN(pay)) {
        const takeRate = rates.takePercent / 100;
        if (takeRate < 0 || takeRate >= 1) {
          onChange("chargePerVisit", "");
          return;
        }
        const charge = (pay / (1 - takeRate)).toFixed(2);
        onChange("chargePerVisit", charge);
      }
    }
  }

  const formatWorkplaceDateInput = useFormatWorkplaceDateInput();
  const todayIso = formatWorkplaceDateInput(new Date().toISOString());
  const firstVisitDueDateHint = getDeadlineGraceHint(data.firstVisitDueDate);

  const isRegularVisit = visitType === VisitType.REGULAR;

  return (
    <Stack spacing={3}>
      {isRegularVisit && (
        <>
          <Stack spacing={1}>
            <Text semibold variant="body1">
              Visits per week
            </Text>
            <TextField
              type="number"
              value={data.visitsPerWeek}
              onChange={(e) => {
                onChange("visitsPerWeek", e.target.value);
              }}
              sx={inputSx}
              inputProps={{ min: 1 }}
            />
          </Stack>

          <Stack spacing={1}>
            <Text semibold variant="body1">
              Total weeks
            </Text>
            <TextField
              type="number"
              value={data.totalWeeks}
              onChange={(e) => {
                onChange("totalWeeks", e.target.value);
              }}
              sx={inputSx}
              inputProps={{ min: 1 }}
            />
          </Stack>
        </>
      )}

      {pricingType === PricingType.PER_HOUR && (
        <Stack spacing={1}>
          <Text semibold variant="body1">
            Estimated duration
          </Text>
          <TextField
            type="number"
            value={data.estimatedDuration}
            onChange={(e) => {
              onChange("estimatedDuration", e.target.value);
            }}
            sx={inputSx}
            helperText="Minimum 2 hours"
            inputProps={{ min: 0, step: 0.5 }}
            // eslint-disable-next-line react/jsx-no-duplicate-props -- endAdornment needs InputProps, min/step need inputProps
            InputProps={{ endAdornment: "hours" }}
          />
        </Stack>
      )}

      <Stack spacing={1}>
        <Text semibold variant="body1">
          First visit due date
        </Text>
        <TextField
          type="date"
          value={data.firstVisitDueDate}
          onChange={(e) => {
            onChange("firstVisitDueDate", e.target.value);
          }}
          sx={inputSx}
          inputProps={{ min: todayIso }}
        />
        {firstVisitDueDateHint ? (
          <Text variant="body2" color="text.secondary">
            {firstVisitDueDateHint}
          </Text>
        ) : null}
      </Stack>

      {rates.canSetRates && (
        <>
          <Stack spacing={1}>
            <Text semibold variant="body1">{`Charge ${rateLabel}`}</Text>
            <TextField
              type="number"
              placeholder={`${rates.chargeRate.recommendedRate.toFixed(2)} (recommended)`}
              value={data.chargePerVisit}
              onChange={(e) => {
                handleChargeChange(e.target.value);
              }}
              sx={inputSx}
              inputProps={{
                min: rates.chargeRate.minimum,
                max: rates.chargeRate.maximum,
                step: 0.01,
              }}
            />
          </Stack>

          <Stack spacing={1}>
            <Text semibold variant="body1">{`Pay ${rateLabel}`}</Text>
            <TextField
              type="number"
              placeholder={rates.payRate.recommendedRate.toFixed(2)}
              value={data.payPerVisit}
              onChange={(e) => {
                handlePayChange(e.target.value);
              }}
              sx={inputSx}
              inputProps={{
                min: rates.payRate.minimum,
                max: rates.payRate.maximum,
                step: 0.01,
              }}
            />
          </Stack>
        </>
      )}

      <ControlledModificationReasonField
        value={data.modificationReason}
        onChange={(value) => {
          onChange("modificationReason", value);
        }}
      />
    </Stack>
  );
}
